import { MoreVertical, Pencil, Plus, Trash2 } from "lucide-react";
import type React from "react";
import type { WorkspaceItem } from "@/api/knowledgeGraphApi";
import UserProfileWidget from "@/features/notebook/components/UserProfileWidget";
import { useWorkspaceMenu } from "@/features/notebook/hooks/useWorkspaceMenu";
import knowgemGem from "@/images/knowgem-gem.svg";

const CARD_COLORS = [
	"#dbeafe", // blue-100
	"#ede9fe", // violet-100
	"#dcfce7", // green-100
	"#fef9c3", // yellow-100
	"#fce7f3", // pink-100
	"#ffedd5", // orange-100
	"#e0f2fe", // sky-100
	"#f3e8ff", // purple-100
];

const formatDate = (dateStr: string) =>
	new Date(dateStr).toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	});

// ── Sub-components ────────────────────────────────────────────────────────────

interface CreateCardProps {
	createPending?: boolean;
	onClick: () => void;
}

const CreateWorkspaceCard: React.FC<CreateCardProps> = ({ createPending, onClick }) => (
	<button
		type="button"
		onClick={onClick}
		disabled={createPending}
		className="group flex flex-col items-center justify-center gap-3 h-44 rounded-2xl border-2 border-dashed transition-colors disabled:opacity-50 cursor-pointer"
		style={{ borderColor: "var(--border-strong)" }}
	>
		<div
			className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
			style={{ backgroundColor: "var(--surface-3)" }}
		>
			<Plus className="w-5 h-5" style={{ color: "var(--primary)" }} />
		</div>
		<span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
			New workspace
		</span>
	</button>
);

interface WorkspaceCardMenuProps {
	isOpen: boolean;
	menuRef: React.MutableRefObject<HTMLDivElement | null>;
	onToggle: (e: React.MouseEvent) => void;
	onRename: (e: React.MouseEvent) => void;
	onDelete: (e: React.MouseEvent) => void;
}

const WorkspaceCardMenu: React.FC<WorkspaceCardMenuProps> = ({ isOpen, menuRef, onToggle, onRename, onDelete }) => (
	<div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
		<div
			className="relative"
			ref={(el) => {
				if (isOpen) menuRef.current = el;
			}}
		>
			<button
				type="button"
				onClick={onToggle}
				className="p-1.5 rounded-lg transition-colors"
				style={{ backgroundColor: "rgba(255,255,255,0.75)" }}
				aria-label="Workspace options"
			>
				<MoreVertical className="w-4 h-4 text-gray-600" />
			</button>

			{isOpen && (
				<div
					className="absolute right-0 top-8 z-20 rounded-lg shadow-lg min-w-[120px] border"
					style={{ backgroundColor: "var(--surface)", borderColor: "var(--border-strong)" }}
				>
					<button
						type="button"
						onClick={onRename}
						className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-t-lg soft-hover"
						style={{ color: "var(--text-muted)" }}
					>
						<Pencil className="w-4 h-4" />
						Rename
					</button>
					<button
						type="button"
						onClick={onDelete}
						className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
					>
						<Trash2 className="w-4 h-4" />
						Delete
					</button>
				</div>
			)}
		</div>
	</div>
);

interface WorkspaceCardProps {
	ws: WorkspaceItem;
	bgColor: string;
	isMenuOpen: boolean;
	menuRef: React.MutableRefObject<HTMLDivElement | null>;
	onSelect: () => void;
	onMenuToggle: (e: React.MouseEvent) => void;
	onRename: (wsId: string) => void;
	onDelete: (wsId: string, name: string) => void;
	onMenuClose: () => void;
}

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({
	ws,
	bgColor,
	isMenuOpen,
	menuRef,
	onSelect,
	onMenuToggle,
	onRename,
	onDelete,
	onMenuClose,
}) => (
	<div className="relative group">
		<button
			type="button"
			onClick={onSelect}
			className="w-full h-44 rounded-2xl text-left flex flex-col justify-between p-5 transition-all hover:scale-[1.02] shadow-sm hover:shadow-md"
			style={{ backgroundColor: bgColor }}
		>
			<div />
			<div>
				<div className="font-semibold text-gray-900 truncate leading-tight">{ws.name}</div>
				<div className="text-xs text-gray-500 mt-1">{formatDate(ws.createdAt)}</div>
			</div>
		</button>

		<WorkspaceCardMenu
			isOpen={isMenuOpen}
			menuRef={menuRef}
			onToggle={onMenuToggle}
			onRename={(e) => {
				e.stopPropagation();
				onMenuClose();
				onRename(ws.id);
			}}
			onDelete={(e) => {
				e.stopPropagation();
				onMenuClose();
				onDelete(ws.id, ws.name);
			}}
		/>
	</div>
);

// ── Main component ────────────────────────────────────────────────────────────

interface WorkspaceGridProps {
	workspacesData: WorkspaceItem[] | undefined;
	isLoading: boolean;
	onWorkspaceSelect: (workspaceId: string) => void;
	onCreateWorkspace: (name: string) => void;
	onRenameWorkspace: (workspaceId: string) => void;
	onDeleteWorkspace: (workspaceId: string, name: string) => void;
	createPending?: boolean;
}

const WorkspaceGrid: React.FC<WorkspaceGridProps> = ({
	workspacesData,
	isLoading,
	onWorkspaceSelect,
	onCreateWorkspace,
	onRenameWorkspace,
	onDeleteWorkspace,
	createPending,
}) => {
	const { menuOpenId, setMenuOpenId, menuRef } = useWorkspaceMenu();

	const handleCreateClick = () => {
		const name = window.prompt("New workspace name:");
		if (name?.trim()) onCreateWorkspace(name.trim());
	};

	return (
		<div className="min-h-screen" style={{ backgroundColor: "var(--bg-alt)" }}>
			<header
				className="border-b px-6 py-3 flex items-center justify-between"
				style={{ backgroundColor: "var(--surface)", borderColor: "var(--border-strong)" }}
			>
				<div className="flex items-center gap-3">
					<img src={knowgemGem} alt="KnowGem Logo" className="h-6 w-6" />
					<span className="text-base font-semibold" style={{ color: "var(--text)" }}>
						KnowGem
					</span>
				</div>
				<UserProfileWidget />
			</header>

			<main className="max-w-6xl mx-auto px-6 py-10">
				<h2 className="text-xl font-bold mb-8" style={{ color: "var(--text)" }}>
					My Workspaces
				</h2>

				{isLoading ? (
					<p className="text-sm" style={{ color: "var(--text-muted)" }}>
						Loading workspaces...
					</p>
				) : (
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
						<CreateWorkspaceCard createPending={createPending} onClick={handleCreateClick} />

						{workspacesData?.map((ws, idx) => (
							<WorkspaceCard
								key={ws.id}
								ws={ws}
								bgColor={CARD_COLORS[idx % CARD_COLORS.length]}
								isMenuOpen={menuOpenId === ws.id}
								menuRef={menuRef}
								onSelect={() => {
									setMenuOpenId(null);
									onWorkspaceSelect(ws.id);
								}}
								onMenuToggle={(e) => {
									e.stopPropagation();
									setMenuOpenId((prev) => (prev === ws.id ? null : ws.id));
								}}
								onRename={onRenameWorkspace}
								onDelete={onDeleteWorkspace}
								onMenuClose={() => setMenuOpenId(null)}
							/>
						))}
					</div>
				)}
			</main>
		</div>
	);
};

export default WorkspaceGrid;
